/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Page Cashback selectors
 *
 * @author Yakov Gushchin
 * @version 1.13.1
 * @since 1.12.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { IOptionItemProps } from '../../../components/common/Select/Select';
import { IRootReducer } from '../../../reducers/rootReducer';
import { pageCashbackInitialState } from '../pageCashbackConstants';
import { IPageCashbackReducer } from '../pageCashbackReducer';
import { ICashbackMeta } from '../types/cashbackTypes';

export const makeSelectCashback = (state: IRootReducer): IPageCashbackReducer =>
    state.cashback || pageCashbackInitialState;

/*
* -------------------------------------------
*           Page statuses
* -------------------------------------------
*/

export const makeSelectCashbackIsLoading = ():
    OutputSelector<IRootReducer, boolean, (state: IPageCashbackReducer) => boolean> =>
    createSelector(makeSelectCashback, (state) => state.isLoading);

export const makeSelectCashbackIsError = ():
    OutputSelector<IRootReducer, boolean, (state: IPageCashbackReducer) => boolean> =>
    createSelector(makeSelectCashback, (state) => state.isError);

/*
* -------------------------------------------
*           Meta
* -------------------------------------------
*/

export const makeSelectCashbackMeta = ():
    OutputSelector<IRootReducer, ICashbackMeta, (state: IPageCashbackReducer) => ICashbackMeta> =>
    createSelector(makeSelectCashback, (state) => state.meta);

export const makeSelectCashbackClientId = ():
    OutputSelector<IRootReducer, string | undefined, (state: IPageCashbackReducer) => string | undefined> =>
    createSelector(makeSelectCashback, (state) => state.clientId);

/*
* -------------------------------------------
*           Account Select
* -------------------------------------------
*/

export const makeSelectCashbackSelectCurrent = ():
    OutputSelector<IRootReducer, IOptionItemProps, (state: IPageCashbackReducer) => IOptionItemProps> =>
    createSelector(makeSelectCashback, (state) => state.accountSelectCurrent);

export const makeSelectCashbackSelectHasMore = ():
    OutputSelector<IRootReducer, boolean, (state: IPageCashbackReducer) => boolean> =>
    createSelector(makeSelectCashback, (state) => state.accountSelectHasMore);

export const makeSelectCashbackSelectIsLoading = ():
    OutputSelector<IRootReducer, boolean, (state: IPageCashbackReducer) => boolean> =>
    createSelector(makeSelectCashback, (state) => state.accountSelectIsLoading);

export const makeSelectCashbackSelectList = ():
    OutputSelector<IRootReducer, IOptionItemProps[], (state: IPageCashbackReducer) => IOptionItemProps[]> =>
    createSelector(makeSelectCashback, (state) => state.accountSelectList);
